/**
 * Keyboard input source for SmashParty.
 *
 * Uses KeyboardEvent.code (layout-independent physical key) — NOT .key (printable character).
 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
 *
 * Attaches a single keydown + keyup listener to the injected EventTarget (defaults to
 * window). Maintains a Set<string> of currently-held KeyboardEvent.code values.
 * isPressed() and getMovement() are O(1) via Set.has().
 *
 * P1 KeyE dual-bind (spec §10 lines 744+753):
 *   KEY_MAP.p1.item === 'KeyE' AND KEY_MAP.p1.nextItem === 'KeyE'.
 *   When KeyE is held both isPressed('p1','item') and isPressed('p1','nextItem') return true.
 *   Disambiguation by game-phase context is the consumer's responsibility.
 */

import { KEY_MAP, type KeyboardLayout } from './key-map';
import type { Vec2 } from './types';

export type LogicalKey =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'action'
  | 'item'
  | 'confirm'
  | 'back'
  | 'pause'
  | 'viewMap'
  | 'scoreboard'
  | 'prevItem'
  | 'nextItem';

export interface KeyboardSource {
  isPressed(playerId: KeyboardLayout, logicalKey: LogicalKey): boolean;
  getMovement(playerId: KeyboardLayout): Vec2;
  dispose(): void;
}

/**
 * Resolve a logical key to the physical KeyboardEvent.code string for a given layout.
 * Returns undefined when the layout has no binding for the logical key (e.g. P2 prevItem).
 */
function resolveCode(layout: KeyboardLayout, logicalKey: LogicalKey): string | undefined {
  const entry = KEY_MAP[layout];
  switch (logicalKey) {
    case 'up':
      return entry.move.up;
    case 'down':
      return entry.move.down;
    case 'left':
      return entry.move.left;
    case 'right':
      return entry.move.right;
    case 'action':
      return entry.action;
    case 'item':
      return entry.item;
    case 'prevItem':
      return entry.prevItem; // may be undefined for P2
    case 'nextItem':
      return entry.nextItem; // may be undefined for P2
    // 'confirm', 'back', 'pause', 'viewMap', 'scoreboard' have no keyboard binding in
    // the current KEY_MAP (gamepad-only). Return undefined so isPressed() returns false.
    default:
      return undefined;
  }
}

export function createKeyboardSource(target: EventTarget = window): KeyboardSource {
  const held = new Set<string>();
  let disposed = false;

  const handleDown = (e: Event): void => {
    const code = (e as KeyboardEvent).code;
    if (code) held.add(code);
  };

  const handleUp = (e: Event): void => {
    const code = (e as KeyboardEvent).code;
    if (code) held.delete(code);
  };

  target.addEventListener('keydown', handleDown);
  target.addEventListener('keyup', handleUp);

  return {
    isPressed(playerId: KeyboardLayout, logicalKey: LogicalKey): boolean {
      if (disposed) return false;
      const code = resolveCode(playerId, logicalKey);
      if (code === undefined) return false;
      return held.has(code);
    },

    getMovement(playerId: KeyboardLayout): Vec2 {
      if (disposed) return { x: 0, y: 0 };
      const entry = KEY_MAP[playerId];
      const right = held.has(entry.move.right) ? 1 : 0;
      const left = held.has(entry.move.left) ? 1 : 0;
      const up = held.has(entry.move.up) ? 1 : 0;
      const down = held.has(entry.move.down) ? 1 : 0;
      return { x: right - left, y: up - down };
    },

    dispose(): void {
      if (disposed) return; // idempotent
      disposed = true;
      target.removeEventListener('keydown', handleDown);
      target.removeEventListener('keyup', handleUp);
      held.clear();
    },
  };
}
